<script setup>
import { userAuthState } from '@/@core/auth'
import { cookieRef } from '@/@layouts/stores/config'
import { useDisplay } from 'vuetify'
import ActionHubOverview from '@/views/feedback/action-hub/ActionHubOverview.vue'
import ActionHubRail from '@/views/feedback/action-hub/ActionHubRail.vue'
import ActionHubStream from '@/views/feedback/action-hub/ActionHubStream.vue'
import HubListSkeleton from '@/views/feedback/action-hub/HubListSkeleton.vue'
import { SECTIONS, sectionByKey } from '@/views/feedback/action-hub/sections'
import FeedbackDrawerV2 from '@/views/feedback/feedback-panel/drawer/FeedbackDrawerV2.vue'
import FeedbackResponseDrawer from '@/views/feedback/feedback-panel/drawer/FeedbackResponseDrawer.vue'
import PreferenceDrawer from '@/views/feedback/preference/PreferenceDrawer.vue'
import TicketDrawer from '@/views/feedback/ticket-management/drawer/TicketDrawerV2.vue'

definePage({
  meta: {
    name: 'action-hub',
    title: 'Action Hub',
  },
})

const VIEWS = ['overview', 'queues']

// The view was called 'eagle' before it was renamed; an old link still opens it.
const VIEW_ALIASES = { eagle: 'overview' }
const normaliseView = value => VIEW_ALIASES[value] ?? value

const route = useRoute()
const router = useRouter()

// Phones (< 600px): the view switch moves to a bar at the foot of the screen and the
// scope control fills the header.
const { xs: isPhone } = useDisplay()

const selectedUnit = cookieRef('selectedUnit')
const scope = ref('mine')

const context = computed(() => ({
  userId: userAuthState().user?.id,
  unitId: selectedUnit.value ?? null,
  mine: scope.value === 'mine',
}))

// ---- View: the URL, else the Overview. Opening the hub always lands on the Overview;
// only a link that says ?view=queues (as the toggle writes) opens the Queues.
const view = computed({
  get: () => {
    const requested = normaliseView(route.query.view)

    return VIEWS.includes(requested) ? requested : 'overview'
  },
  set: value => router.replace({ query: { ...route.query, view: value } }),
})

// ---- Counts (queue rail, tab validation). useApi unwraps `{ data }` responses
// that carry no `meta`, so this is already the counts map.
const summaryQuery = computed(() => ({ scope: scope.value, unit_id: selectedUnit.value ?? null }))

const {
  data: summaryData,
  isFetching: isSummaryFetching,
  error: summaryError,
  execute: fetchSummary,
} = useApi(createUrl('/feedbacks/action-hub/summary', { query: summaryQuery }))

const counts = computed(() => summaryData.value ?? {})

// An AbortError from a superseded request (scope or unit changed mid-flight) is
// left in `error` by vueuse after the newer request succeeds; it is not a failure.
const summaryFailed = computed(() => !!summaryError.value && summaryError.value.name !== 'AbortError')

// First load, until the counts or a failure arrive. Not `isFetching`: an aborted
// request flips that to false while its replacement is still in flight.
const isSummaryLoading = computed(() => !summaryData.value && !summaryFailed.value)

// With no counts to list the queues from, the rail falls back to every section so
// the user can still switch queues. The counts are unknown, not zero, so they are
// null and the rail shows no number.
const railCounts = computed(() => (summaryFailed.value && !summaryData.value
  ? Object.fromEntries(SECTIONS.map(section => [section.key, null]))
  : counts.value))

// Which keys come back depends only on permissions, never on scope.
const hasNoSections = computed(() => summaryData.value && Object.keys(counts.value).length === 0)

// ---- Selected queue (Queues view), kept in the URL.
const requestedTab = computed(() => (typeof route.query.tab === 'string' ? route.query.tab : null))

const tab = computed({
  get: () => {
    if (summaryFailed.value && !summaryData.value)
      return sectionByKey(requestedTab.value) ? requestedTab.value : SECTIONS[0].key

    return requestedTab.value && requestedTab.value in counts.value ? requestedTab.value : null
  },
  set: value => router.replace({ query: { ...route.query, tab: value } }),
})

watch([counts, requestedTab, view], ([value]) => {
  if (view.value !== 'queues' || tab.value || summaryFailed.value)
    return

  const visible = SECTIONS.filter(section => section.key in value)
  const first = visible.find(section => value[section.key] > 0) ?? visible[0]

  if (first)
    tab.value = first.key
}, { immediate: true })

const section = computed(() => sectionByKey(tab.value))

const openQueue = key => {
  router.replace({ query: { ...route.query, view: 'queues', tab: key } })
}

// ---- Drawers
const refStream = ref()
const refOverview = ref()
const ticketId = ref(null)
const capaId = ref(null)
const isTicketDrawerVisible = ref(false)
const feedbackDrawerData = ref()
const isFeedbackDrawerVisible = ref(false)
const isCallbackDrawer = ref(false)
const feedbackResponseDrawerData = ref()
const isFeedbackResponseDrawerVisible = ref(false)

const openTicket = (id, capa = null) => {
  ticketId.value = Number(id)
  capaId.value = capa === null ? null : Number(capa)
  isTicketDrawerVisible.value = true
}

const openFeedback = (feedback, asCallback) => {
  isCallbackDrawer.value = asCallback
  feedbackDrawerData.value = asCallback ? { ...feedback, drawerType: 'customer-communication' } : feedback
  isFeedbackDrawerVisible.value = true
}

// The drawer, not the Preference page: the page needs its own menu permission,
// which someone who may acknowledge preferences does not necessarily have.
const preferenceCustomer = ref(null)
const isPreferenceDrawerVisible = ref(false)

const openPreferences = customer => {
  preferenceCustomer.value = customer
  isPreferenceDrawerVisible.value = true
}

// A full list item from a card.
const openItem = (item, card) => {
  if (card === 'ticket' || card === 'reassign')
    return openTicket(item.id)
  if (card === 'capa')
    return openTicket(item.id, item.capa_id ?? null)
  if (card === 'preference')
    return openPreferences(item.customer)

  return openFeedback(item, card === 'callback')
}

const showFeedbackResponse = (feedbackData, highlightedFeedbackResponse = null) => {
  feedbackResponseDrawerData.value = { ...feedbackData, highlighted_feedback_response: highlightedFeedbackResponse }
  isFeedbackResponseDrawerVisible.value = true
}

// ---- Refresh
const refreshCounts = () => {
  fetchSummary()
  refOverview.value?.refresh()
}

const refreshAll = () => {
  refStream.value?.refresh()
  refreshCounts()
}

const retrySummary = () => {
  fetchSummary()
}

// Same contract as the ticket page: close the drawer after an update unless the
// drawer passes false, then refresh.
const refreshTicketData = (closeDrawer = true) => {
  // Closing refreshes through the watcher below.
  if (closeDrawer && isTicketDrawerVisible.value)
    isTicketDrawerVisible.value = false
  else
    refreshAll()
}

// Cards only open a drawer, so whatever was done there (a call logged, a reminder
// dismissed) shows in the queue as soon as the drawer closes.
watch([isTicketDrawerVisible, isFeedbackDrawerVisible, isPreferenceDrawerVisible], (now, before) => {
  if (before.some((wasOpen, i) => wasOpen && !now[i]))
    refreshAll()
})

// Coming back to the tab: what was waiting may have changed meanwhile.
const onVisible = () => {
  if (document.visibilityState === 'visible')
    refreshAll()
}

onMounted(() => document.addEventListener('visibilitychange', onVisible))
onBeforeUnmount(() => document.removeEventListener('visibilitychange', onVisible))
</script>

<template>
  <section :class="{ 'hub-page--phone': isPhone }">
    <div class="d-flex align-center flex-wrap gap-3 mb-6">
      <VBtnToggle
        v-if="!isPhone"
        v-model="view"
        mandatory
        density="compact"
        color="primary"
        variant="outlined"
        divided
      >
        <VBtn value="overview">
          <VIcon
            icon="tabler-layout-grid"
            size="16"
            class="me-1"
          />
          Overview
        </VBtn>
        <VBtn value="queues">
          <VIcon
            icon="tabler-list-details"
            size="16"
            class="me-1"
          />
          Queues
        </VBtn>
      </VBtnToggle>

      <VSpacer v-if="!isPhone" />

      <VBtnToggle
        v-model="scope"
        mandatory
        density="compact"
        color="primary"
        variant="outlined"
        divided
        :class="{ 'flex-grow-1': isPhone }"
      >
        <VBtn
          value="mine"
          :class="{ 'flex-grow-1': isPhone }"
        >
          Mine
        </VBtn>
        <VBtn
          value="department"
          :class="{ 'flex-grow-1': isPhone }"
        >
          My department
        </VBtn>
      </VBtnToggle>

      <!-- Same height as the compact toggles beside it. -->
      <VBtn
        variant="tonal"
        height="36"
        :min-width="isPhone ? 36 : undefined"
        :class="{ 'px-0': isPhone }"
        :loading="isSummaryFetching"
        @click="refreshAll"
      >
        <VIcon
          icon="tabler-refresh"
          size="16"
          class="me-md-1"
        />
        <span class="d-none d-md-inline">Refresh</span>
      </VBtn>
    </div>

    <VAlert
      v-if="summaryFailed && !isSummaryFetching && view === 'queues'"
      type="error"
      variant="tonal"
      class="mb-6"
    >
      Could not load the counts.
      <template #append>
        <VBtn
          variant="outlined"
          size="small"
          @click="retrySummary"
        >
          Retry
        </VBtn>
      </template>
    </VAlert>

    <VCard
      v-if="hasNoSections"
      variant="flat"
      class="border"
    >
      <VCardText class="text-center pa-6">
        <VIcon
          icon="tabler-lock"
          size="32"
          color="secondary"
          class="mb-2"
        />
        <div class="text-body-1 font-weight-medium mb-1">
          Nothing here for you
        </div>
        <div class="text-body-2 text-medium-emphasis mb-4">
          None of the Action Hub queues are available to your role.
        </div>
        <VBtn
          variant="tonal"
          :to="{ name: 'app-feedback-dashboard' }"
        >
          Go to the dashboard
        </VBtn>
      </VCardText>
    </VCard>

    <ActionHubOverview
      v-else-if="view === 'overview'"
      ref="refOverview"
      :scope="scope"
      :unit-id="selectedUnit ?? null"
      @open-queue="openQueue"
    />

    <VRow v-else>
      <VCol
        cols="12"
        md="3"
      >
        <ActionHubRail
          v-model="tab"
          :counts="railCounts"
          :loading="isSummaryLoading"
        />
      </VCol>
      <VCol
        cols="12"
        md="9"
      >
        <ActionHubStream
          v-if="section"
          :key="section.key"
          ref="refStream"
          :section="section"
          :context="context"
          :count="counts[section.key]"
          @open="openItem($event, section.card)"
        />

        <!-- Which queue to show is not known until the counts arrive. -->
        <HubListSkeleton v-else-if="isSummaryLoading" />
      </VCol>
    </VRow>

    <TicketDrawer
      v-model:isDrawerOpen="isTicketDrawerVisible"
      :ticket-id="ticketId"
      :capa-id="capaId"
      @refresh-ticket-data="refreshTicketData"
      @show-feedback-response="showFeedbackResponse"
    />

    <FeedbackDrawerV2
      v-model:isDrawerOpen="isFeedbackDrawerVisible"
      :feedback-data="feedbackDrawerData"
      :customer-communication="isCallbackDrawer"
      @show-feedback-response="showFeedbackResponse"
      @refresh-feedback-table="refreshAll"
    />

    <PreferenceDrawer
      v-if="preferenceCustomer"
      v-model:is-drawer-open="isPreferenceDrawerVisible"
      :customer-id="preferenceCustomer.id"
      :customer="preferenceCustomer"
    />

    <FeedbackResponseDrawer
      v-model:isDrawerOpen="isFeedbackResponseDrawerVisible"
      :feedback-data="feedbackResponseDrawerData"
    />

    <!-- Phone: the view switch as a bar at the foot of the screen. -->
    <nav
      v-if="isPhone"
      class="hub-tabs d-flex"
      aria-label="Action Hub view"
    >
      <button
        type="button"
        class="hub-tabs__tab"
        :class="{ 'hub-tabs__tab--active': view === 'overview' }"
        :aria-selected="view === 'overview'"
        @click="view = 'overview'"
      >
        <VIcon
          icon="tabler-layout-grid"
          size="20"
        />
        Overview
      </button>
      <button
        type="button"
        class="hub-tabs__tab"
        :class="{ 'hub-tabs__tab--active': view === 'queues' }"
        :aria-selected="view === 'queues'"
        @click="view = 'queues'"
      >
        <VIcon
          icon="tabler-list-details"
          size="20"
        />
        Queues
      </button>
    </nav>
  </section>
</template>

<style lang="scss" scoped>
// Room for the bar at the foot of the screen.
.hub-page--phone {
  padding-block-end: calc(60px + 16px + env(safe-area-inset-bottom));
}

.hub-tabs {
  position: fixed;
  inset-inline: 0;
  inset-block-end: 0;
  z-index: 1005;
  padding: 0 16px env(safe-area-inset-bottom);
  background: rgb(var(--v-theme-surface));
  border-block-start: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));

  &__tab {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    block-size: 60px;
    margin-block-start: -1px;
    background: none;
    border: 0;
    border-block-start: 2px solid transparent;
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
    font-size: 0.75rem;
    font-weight: 500;
    cursor: pointer;

    &--active {
      border-block-start-color: rgb(var(--v-theme-primary));
      color: rgb(var(--v-theme-primary));
      font-weight: 600;
    }
  }
}
</style>
